import React, { useState, useEffect } from "react";
import {  Modal, Flowbite } from "flowbite-react";
import usersApi from "../services/users/UserApi";
import CommonHeader from "./CommonHeader";
import ToastNotification, { showToast } from "./Toaster";
import PillButton from "./button/Pills";

const ShortCodesPage = () => {
  const [shortCodesList, setShortCodesList] = useState<
    {
      id: number;
      shortCode: string;
      userId: string;
      password: string;
      assigned: boolean;
      user: { id: number; username: string };
      showPassword: boolean;
    }[]
  >([]);
  const [loading, setLoading] = useState(true);
  const [openDeleteModal, setOpenDeleteModal] = useState(false);
  const [selectedShortCode, setSelectedShortCode] = useState<{
    id: string;
    code: string;
    description: string;
  } | null>(null);
  const [openAddEditModal, setOpenAddEditModal] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [shortCodeForm, setShortCodeForm] = useState<{
    id?: string;
    shortCode: string;
    userId: string;
    password: string;
  }>({
    shortCode: "",
    userId: "",
    password: "",
  });
  const [formErrors, setFormErrors] = useState<{
    shortCode?: string;
    userId?: string;
    password?: string;
  }>({});
  const [openApplyModal, setOpenApplyModal] = useState(false);
  const [selectedApplyShortCode, setSelectedApplyShortCode] = useState<{
    id: string;
    shortCode: string;
  } | null>(null);
  const [selectedUserId, setSelectedUserId] = useState("");
  const [userList, setUserList] = useState<{ id: string; name: string }[]>([]);
  const [openUserDetailsModal, setOpenUserDetailsModal] = useState(false);
  const [selectedUserDetails, setSelectedUserDetails] = useState<{
    id: string;
    name: string;
  } | null>(null);

  const fetchShortCodesList = async () => {
    setLoading(true);
    try {
      const response = await usersApi.getShortcode(); // Fetch the short codes list

      if (response.success) {
        const updatedList = response.value.data.contents.map((item: any) => ({
          ...item,
          showPassword: false, // Add showPassword property
        }));
        setShortCodesList(updatedList);
      } else {
        if (
          response.error &&
          typeof response.error === "object" &&
          "message" in response.error
        ) {
          console.error(
            "Failed to fetch short codes:",
            (response.error as { message: string }).message
          );
        } else {
          console.error("Failed to fetch short codes: Unknown error");
        }
      }
    } catch (error) {
      console.error("Error fetching short codes:", error);
    } finally {
      setLoading(false);
    }
  };

  const fetchUserList = async () => {
    try {
      const response = await usersApi.getNoShortcodesUser(); // Fetch the user list
      if (response.success) {
        const assignedUserIds = shortCodesList
          .filter((shortCode) => shortCode.assigned)
          .map((shortCode) => shortCode.user.id.toString());
        console.log(
          "assignedUserIds",
          assignedUserIds,
          response.value.data.contents
        );
        setUserList(
          response.value.data.contents
            .filter(
              (user: any) => !assignedUserIds.includes(user.id.toString())
            )
            .map((user: any) => ({
              id: user.id.toString(),
              name: user.username,
            }))
        );
      } else {
        console.error("Failed to fetch user list");
      }
    } catch (error) {
      console.error("Error fetching user list:", error);
    }
  };

  const handleUnassignShortCode = async (selectedUserDetails: {
    id: number;
    name: string;
  }): Promise<void> => {
    if (selectedUserDetails) {
      try {
        const response = await usersApi.unassignShortcode(
          selectedUserDetails.id
        );
        console.log(response, "response");
        if (response.success) {
          showToast("Short code unassigned successfully!", "success");
        } else {
          console.error("Failed to unassign short code");
        }
      } catch (error) {
        console.error("Error unassigning short code:", error);
      } finally {
        setOpenUserDetailsModal(false);
        fetchShortCodesList(); // Refresh the list
      }
    }
  };

  useEffect(() => {
    fetchShortCodesList();
  }, []);

  const handleDeleteAPI = async () => {
    if (selectedShortCode) {
      try {
        const response = await usersApi.deleteShortcode(selectedShortCode.id);
        if (response.success) {
          setShortCodesList((prevList) =>
            prevList.filter((sc) => sc.id !== Number(selectedShortCode.id))
          );
          setOpenDeleteModal(false);
          showToast("Short code deleted successfully!", "success");
        } else {
          if (
            response.error &&
            typeof response.error === "object" &&
            "message" in response.error
          ) {
            console.error(
              "Failed to delete short code:",
              (response.error as { message: string }).message
            );
          } else {
            console.error("Failed to delete short code: Unknown error");
          }
        }
      } catch (error) {
        console.error("Error deleting short code:", error);
      }
    }
  };

  const handleDeleteClick = (shortCode: {
    id: string;
    code: string;
    description: string;
  }) => {
    setSelectedShortCode(shortCode);
    setOpenDeleteModal(true);
  };

  const handleAddClick = () => {
    setIsEditing(false);
    setShortCodeForm({ shortCode: "", userId: "", password: "" });
    setOpenAddEditModal(true);
  };

  const handleEditClick = (shortCode: {
    id: string;
    shortCode: string;
    userId: string;
    password: string;
  }) => {
    setIsEditing(true);
    setShortCodeForm(shortCode);
    setOpenAddEditModal(true);
  };

  const handleApplyClick = (shortCode: { id: string; shortCode: string }) => {
    setSelectedApplyShortCode(shortCode);
    setOpenApplyModal(true);
    fetchUserList(); // Fetch the user list when opening the modal
  };

  const handleSaveShortCode = async () => {
    // Validation
    const errors: { shortCode?: string; userId?: string; password?: string } =
      {};
    if (!shortCodeForm.shortCode.trim()) {
      errors.shortCode = "Short Code is required!";
    }
    if (!shortCodeForm.userId.trim()) {
      errors.userId = "User ID is required!";
    }
    if (!shortCodeForm.password.trim()) {
      errors.password = "Password is required!";
    }

    if (Object.keys(errors).length > 0) {
      setFormErrors(errors);
      return;
    }

    setFormErrors({}); // Clear errors if validation passes

    try {
      const payload = {
        shortCode: shortCodeForm.shortCode,
        userId: shortCodeForm.userId,
        password: atob(shortCodeForm.password), // Encode password to Base64
      };

      const response = isEditing
        ? await usersApi.updateShortcode({ ...payload, id: shortCodeForm.id })
        : await usersApi.setShortcode(payload);

      if (response.success) {
        fetchShortCodesList(); // Refresh the list
        setOpenAddEditModal(false);
        showToast(
          `${
            isEditing ? "Short code updated" : "Short code added"
          } successfully!`,
          "success"
        );
      } else {
        if (
          response.error &&
          typeof response.error === "object" &&
          "message" in response.error
        ) {
          console.error(
            "Failed to save short code:",
            (response.error as { message: string }).message
          );
          showToast(
            `Failed to save short code: ${(response.error as { message: string }).message}`,
            "error"
          );
        } else {
          console.error("Failed to save short code: Unknown error");
          showToast("Failed to save short code: Unknown error", "error");
        }
      }
    } catch (error) {
      console.error("Error saving short code:", error);
    }
  };

  const handleApplyShortCode = async () => {
    if (selectedApplyShortCode && selectedUserId) {
      try {
        const response = await usersApi.applyShortcode({
          shortCodeId: selectedApplyShortCode.id,
          userId: selectedUserId,
        });
        if (response.success) {
          console.log("Short code applied successfully");
          showToast("Short code applied successfully!", "success");
          fetchShortCodesList(); // Refresh the list
          setOpenApplyModal(false);
        } else {
          console.error("Failed to apply short code");
        }
      } catch (error) {
        console.error("Error applying short code:", error);
      }
    }
  };

  const togglePasswordVisibility = (id: number) => {
    setShortCodesList((prevList) =>
      prevList.map((item) =>
        item.id === id ? { ...item, showPassword: !item.showPassword } : item
      )
    );
  };

  return (
    <Flowbite>
      <div className="flex w-full h-[calc(100vh-67px)]">
        <ToastNotification />
        <div className="flex-1">
          <CommonHeader
            title="Short Codes"
            buttonText="Add Short Code"
            onButtonClick={handleAddClick}
          />

          {loading ? (
            <p className="text-ez-text-muted text-center mt-[200px]">
              Loading...
            </p>
          ) : shortCodesList && shortCodesList.length === 0 ? (
            <p className="text-ez-text-muted text-center">No records found.</p>
          ) : (
            <div className="w-full px-16 py-6">
              <div className="relative overflow-x-auto border border-ez-border bg-ez-surface shadow-sm">
                <table className="w-full text-sm text-left rtl:text-right text-ez-text-secondary dark:text-ez-text-primary">
                  <thead className="text-xs uppercase bg-ez-elevated dark:bg-ez-elevated dark:text-ez-text-secondary text-ez-text-secondary">
                    <tr>
                      <th scope="col" className="px-6 py-3">
                        Code
                      </th>
                      <th scope="col" className="px-6 py-3">
                        User ID
                      </th>
                      <th scope="col" className="px-6 py-3">
                        Password
                      </th>
                      <th scope="col" className="px-6 py-3">
                        Assigned
                      </th>
                      <th scope="col" className="px-6 py-3">
                        Action
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {shortCodesList.map((shortCode) => (
                      <tr
                        key={shortCode.id}
                        className="border-b border-ez-border odd:bg-ez-surface even:bg-ez-elevated hover:bg-violet-50 dark:hover:bg-violet-900/20 transition-colors"
                      >
                        <th
                          scope="row"
                          className="px-6 py-4 font-medium text-ez-text-primary whitespace-nowrap"
                        >
                          {shortCode.shortCode}
                        </th>
                        <td className="px-6 py-4 text-ez-text-secondary">
                          {shortCode.userId}
                        </td>
                        <td className="px-6 py-4 text-ez-text-secondary">
                          <div className="w-[100px] flex items-center justify-end">
                            {shortCode.showPassword
                              ? atob(shortCode.password) // Encode password to Base64
                              : "******"}
                            <button
                              type="button"
                              className="ml-2"
                              onClick={() =>
                                togglePasswordVisibility(shortCode.id)
                              }
                            >
                              {shortCode.showPassword ? (
                                <svg
                                  stroke="currentColor"
                                  fill="currentColor"
                                  strokeWidth="0"
                                  viewBox="0 0 1024 1024"
                                  height="18px"
                                  width="18px"
                                  xmlns="http://www.w3.org/2000/svg"
                                >
                                  <path d="M396 512a112 112 0 1 0 224 0 112 112 0 1 0-224 0zm546.2-25.8C847.4 286.5 704.1 186 512 186c-192.2 0-335.4 100.5-430.2 300.3a60.3 60.3 0 0 0 0 51.5C176.6 737.5 319.9 838 512 838c192.2 0 335.4-100.5 430.2-300.3 7.7-16.2 7.7-35 0-51.5zM508 688c-97.2 0-176-78.8-176-176s78.8-176 176-176 176 78.8 176 176-78.8 176-176 176z"></path>
                                </svg>
                              ) : (
                                <svg
                                  stroke="currentColor"
                                  fill="currentColor"
                                  strokeWidth="0"
                                  viewBox="0 0 1024 1024"
                                  height="18px"
                                  width="18px"
                                  xmlns="http://www.w3.org/2000/svg"
                                >
                                  <path d="M508 624a112 112 0 0 0 112-112c0-3.28-.15-6.53-.43-9.74L498.26 623.57c3.21.28 6.45.43 9.74.43zm370.72-458.44L836 122.88a8 8 0 0 0-11.31 0L715.37 232.23Q624.91 186 512 186q-288.3 0-430.2 300.3a60.3 60.3 0 0 0 0 51.5q56.7 119.43 136.55 191.45L112.56 835a8 8 0 0 0 0 11.31L155.25 889a8 8 0 0 0 11.31 0l712.16-712.12a8 8 0 0 0 0-11.32zM332 512a176 176 0 0 1 258.88-155.28l-48.62 48.62a112.08 112.08 0 0 0-140.92 140.92l-48.62 48.62A175.09 175.09 0 0 1 332 512z"></path>
                                  <path d="M942.2 486.2Q889.4 375 816.51 304.85L672.37 449A176.08 176.08 0 0 1 445 676.37L322.74 798.63Q407.82 838 512 838q288.3 0 430.2-300.3a60.29 60.29 0 0 0 0-51.5z"></path>
                                </svg>
                              )}
                            </button>
                          </div>
                        </td>
                        <td className="px-6 py-4">
                          {shortCode.assigned ? (
                            <button
                              type="button"
                              className="text-violet-600 underline"
                              onClick={() => {
                                setSelectedUserDetails({
                                  id: shortCode.user?.id.toString(),
                                  name: shortCode.user.username,
                                });
                                setOpenUserDetailsModal(true);
                              }}
                            >
                              Assigned
                            </button>
                          ) : (
                            <button
                              type="button"
                              className="text-red-500 underline"
                              onClick={() =>
                                handleApplyClick({
                                  id: shortCode.id.toString(),
                                  shortCode: shortCode.shortCode,
                                })
                              }
                            >
                              Unassigned
                            </button>
                          )}
                        </td>
                        <td className="px-6 py-4">
                          <button
                            type="button"
                            title="Delete short code"
                            className="action-btn"
                            onClick={() =>
                              handleDeleteClick({
                                id: shortCode.id.toString(),
                                code: shortCode.shortCode,
                                description: shortCode.userId, // Adjust as needed
                              })
                            }
                          >
                            <svg
                              stroke="currentColor"
                              fill="currentColor"
                              strokeWidth="0"
                              viewBox="0 0 448 512"
                              height="1em"
                              width="1em"
                              xmlns="http://www.w3.org/2000/svg"
                            >
                              <path d="M432 32H312l-9.4-18.7A24 24 0 0 0 281.1 0H166.8a23.72 23.72 0 0 0-21.4 13.3L136 32H16A16 16 0 0 0 0 48v32a16 16 0 0 0 16 16h416a16 16 0 0 0 16-16V48a16 16 0 0 0-16-16zM53.2 467a48 48 0 0 0 47.9 45h245.8a48 48 0 0 0 47.9-45L416 128H32z"></path>
                            </svg>
                          </button>
                          <button
                            type="button"
                            title="Edit short code"
                            className="action-btn ml-2"
                            onClick={() =>
                              handleEditClick({
                                id: shortCode.id.toString(),
                                shortCode: shortCode.shortCode,
                                userId: shortCode.userId,
                                password: shortCode.password,
                              })
                            }
                          >
                            <svg
                              stroke="currentColor"
                              fill="none"
                              stroke-width="2"
                              viewBox="0 0 24 24"
                              stroke-linecap="round"
                              stroke-linejoin="round"
                              height="15px"
                              width="15px"
                              xmlns="http://www.w3.org/2000/svg"
                            >
                              <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"></path>
                              <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"></path>
                            </svg>
                          </button>
                          <button
                            type="button"
                            className="action-btn ml-2"
                            title="Apply short code"
                            onClick={() =>
                              handleApplyClick({
                                id: shortCode.id.toString(),
                                shortCode: shortCode.shortCode,
                              })
                            }
                          >
                            <svg
                              stroke="currentColor"
                              fill="currentColor"
                              stroke-width="0"
                              viewBox="0 0 1024 1024"
                              height="16px"
                              width="16px"
                              xmlns="http://www.w3.org/2000/svg"
                            >
                              <path d="M512 64C264.6 64 64 264.6 64 512s200.6 448 448 448 448-200.6 448-448S759.4 64 512 64zm192 472c0 4.4-3.6 8-8 8H544v152c0 4.4-3.6 8-8 8h-48c-4.4 0-8-3.6-8-8V544H328c-4.4 0-8-3.6-8-8v-48c0-4.4 3.6-8 8-8h152V328c0-4.4 3.6-8 8-8h48c4.4 0 8 3.6 8 8v152h152c4.4 0 8 3.6 8 8v48z"></path>
                            </svg>
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </div>

        <Modal
          show={openDeleteModal}
          size="md"
          className="fixed inset-0 flex items-center justify-center z-50 bg-black bg-opacity-70 modal-center"
          onClose={() => setOpenDeleteModal(false)}
          popup
        >
                  <Modal.Header className="bg-ez-elevated">
            <h3 className="mb-0 text-lg ml-1 text-ez-text-primary text-bold">
              Delete Short Code
            </h3>
          </Modal.Header>
          <Modal.Body className="bg-ez-surface">
            <div className="text-center mt-5">
              <h3 className="mb-5 text-lg font-normal text-ez-text-secondary">
                Are you sure you want to delete this short code?
              </h3>
              <div className="flex justify-center gap-3">
                <PillButton
                  className="w-[80px] bg-red-500 text-white"
                  onClick={handleDeleteAPI}
                >
                  Yes
                </PillButton>
                <PillButton
                variant="secondary"
                  onClick={() => setOpenDeleteModal(false)}
                >
                  Cancel
                </PillButton>
              </div>
            </div>
          </Modal.Body>
        </Modal>

        <Modal
          show={openAddEditModal}
          size="md"
          className="fixed inset-0 flex items-center justify-center z-50 bg-black bg-opacity-50"
          onClose={() => setOpenAddEditModal(false)}
          popup
        >
          <Modal.Header className="bg-ez-elevated rounded-t-lg">
            <h3 className="text-lg font-bold text-ez-text-primary">
              {isEditing ? "Edit Short Code" : "Add Short Code"}
            </h3>
          </Modal.Header>
          <Modal.Body className="bg-ez-surface">
            <div className="space-y-4">
              <div>
                <label className="block text-ez-text-secondary mb-1">
                  Short Code
                </label>
                <input
                  type="text"
                  placeholder="Short Code"
                  value={shortCodeForm.shortCode}
                  onChange={(e) =>
                    setShortCodeForm({
                      ...shortCodeForm,
                      shortCode: e.target.value,
                    })
                  }
                  className="w-full p-2 border border-ez-border rounded bg-ez-elevated text-ez-text-primary placeholder:text-ez-text-muted"
                />
                {formErrors.shortCode && (
                  <p className="text-red-500 text-sm mt-1">
                    {formErrors.shortCode}
                  </p>
                )}
              </div>
              <div>
                <label className="block text-ez-text-secondary mb-1">
                  User ID
                </label>
                <input
                  type="text"
                  placeholder="User ID"
                  value={shortCodeForm.userId}
                  onChange={(e) =>
                    setShortCodeForm({
                      ...shortCodeForm,
                      userId: e.target.value,
                    })
                  }
                  className="w-full p-2 border border-ez-border rounded bg-ez-elevated text-ez-text-primary placeholder:text-ez-text-muted"
                />
                {formErrors.userId && (
                  <p className="text-red-500 text-sm mt-1">
                    {formErrors.userId}
                  </p>
                )}
              </div>
              <div>
                <label className="block text-ez-text-secondary mb-1">
                  Password
                </label>
                <input
                  type="password"
                  placeholder="Password"
                  value={shortCodeForm.password ? atob(shortCodeForm.password) : ""}
                  onChange={(e) =>
                    setShortCodeForm({
                      ...shortCodeForm,
                      password: btoa(e.target.value),
                    })
                  }
                  className="w-full p-2 border border-ez-border rounded bg-ez-elevated text-ez-text-primary placeholder:text-ez-text-muted"
                />
                {formErrors.password && (
                  <p className="text-red-500 text-sm mt-1">
                    {formErrors.password}
                  </p>
                )}
              </div>
              <div className="flex justify-end gap-3">
                <PillButton
                  className="bg-violet-600 text-white hover:bg-violet-700"
                  onClick={handleSaveShortCode}
                >
                   {isEditing ?"Update": "Save"}
                </PillButton>
                <PillButton
                variant="secondary"
                  onClick={() => setOpenAddEditModal(false)}
                >
                  Cancel
                </PillButton>
              </div>
            </div>
          </Modal.Body>
        </Modal>

        <Modal
          show={openApplyModal}
          size="md"
          className="fixed inset-0 flex items-center justify-center z-50 bg-black bg-opacity-50"
          onClose={() => setOpenApplyModal(false)}
          popup
        >
          <Modal.Header className="bg-ez-elevated rounded-t-lg">
            <h3 className="text-lg font-bold text-ez-text-primary">
              Apply Short Code
            </h3>
          </Modal.Header>
          <Modal.Body className="bg-ez-surface">
            <div className="space-y-4">
              <p className="text-ez-text-secondary">
                Short Code: {selectedApplyShortCode?.shortCode}
              </p>
              <select
                value={selectedUserId}
                onChange={(e) => setSelectedUserId(e.target.value)}
                className="w-full p-2 border border-ez-border rounded bg-ez-elevated text-ez-text-primary"
              >
                <option value="">Select User</option>
                {userList.map((user) => (
                  <option key={user.id} value={user.id}>
                    {user.name}
                  </option>
                ))}
              </select>
              <div className="flex justify-end gap-3">
                <PillButton
                  className="bg-violet-600 text-white hover:bg-violet-700"
                  onClick={handleApplyShortCode}
                >
                  Apply
                </PillButton>
                <PillButton
                variant="secondary"
                  onClick={() => setOpenApplyModal(false)}
                >
                  Cancel
                </PillButton>
              </div>
            </div>
          </Modal.Body>
        </Modal>

        <Modal
          show={openUserDetailsModal}
          size="md"
          className="fixed inset-0 flex items-center justify-center z-50 bg-black bg-opacity-50"
          onClose={() => setOpenUserDetailsModal(false)}
          popup
        >
          <Modal.Header className="bg-ez-elevated rounded-t-lg">
            <h3 className="text-lg font-bold text-ez-text-primary">
              User Details
            </h3>
          </Modal.Header>
          <Modal.Body className="bg-ez-surface">
            <div className="space-y-4">
              <p className="text-ez-text-secondary">
                User ID: {selectedUserDetails?.id}
              </p>
              <p className="text-ez-text-secondary">
                Username: {selectedUserDetails?.name}
              </p>
              <div className="flex justify-end gap-3">
                <PillButton
                  className="dark:bg-red-500 bg-red-500 dark:text-white text-white hover:dark:bg-red-700 hover:bg-red-700"
                  onClick={() => {
                    if (selectedUserDetails) {
                      handleUnassignShortCode({
                        id: Number(selectedUserDetails.id),
                        name: selectedUserDetails.name,
                      });
                    }
                  }}
                >
                  Unassign Short Code
                </PillButton>
                <PillButton
                
                  className="dark:bg-ez-surface hover:dark:bg-ez-elevated text-gray-900 dark:text-ez-text-primary"
                  onClick={() => setOpenUserDetailsModal(false)}
                >
                  Close
                </PillButton>
              </div>
            </div>
          </Modal.Body>
        </Modal>
      </div>
    </Flowbite>
  );
};

export default ShortCodesPage;


